import React, { useContext, useEffect, useReducer, useState } from "react";
import { Form, Input, Tag, Typography } from "@pankod/refine-antd";
// Import from the package main (NOT `rc-field-form/lib/FieldContext`). The
// package ships both CJS (`lib/`) and ESM (`es/`) builds, each with its own
// `React.createContext()` call — meaning if antd resolves to `es/` (the
// default for bundlers via the `module` field) and we import from `lib/`, we
// end up with two *different* Context objects. `useContext(libContext)` then
// can't see the Provider that antd's Form.List set on `esContext`, so our
// prefixName comes back undefined and every nested path read goes to the
// wrong place. Importing from the package main makes our resolution match
// antd's: same Context instance, same Provider, prefix actually flows.
import { FieldContext } from "rc-field-form";

import { ILanguage } from "interfaces";

// Internal HOOK_MARK constant used by rc-field-form's `getInternalHooks`. Not
// re-exported publicly; hardcoded here to avoid importing internal paths that
// would risk the same dual-build mismatch as FieldContext (see comment above).
const HOOK_MARK = "RC_FORM_INTERNAL_HOOKS";

interface TranslatedFieldProps {
  languages: ILanguage[];
  // RELATIVE path — gets prefixed by any enclosing <Form.List> the same way
  // every other Form.Item in the project does. At top level (no Form.List),
  // the prefix is [] so this is effectively absolute.
  fieldPath: (string | number)[];
  multiline?: boolean;
}

const { Text } = Typography;

const getDeep = (obj: any, path: (string | number)[]): any => {
  let v = obj;
  for (const k of path) {
    if (v == null) return undefined;
    v = v[k];
  }
  return v;
};

/**
 * Compact TranslatedString editor. One visible input for the currently
 * selected language plus a row of language tags below; click a tag to switch.
 * Tags are gray when empty / green when filled.
 *
 * Why every language stays mounted (hidden, not unmounted): antd's
 * `getFieldsValue()` — which feeds `onFinish` on submit — only collects
 * values for fields that are CURRENTLY REGISTERED. If only the active
 * language's Form.Item were mounted, every other language's value would be
 * dropped from the submitted payload.
 *
 * Why we subscribe manually with `registerWatch` instead of `Form.useWatch`
 * for the completion state: useWatch and Form.Item shouldUpdate both proved
 * unreliable for fields rendered inside the editor's Modal — even with the
 * correct absolute path. Going directly through `form.getInternalHooks`'s
 * `registerWatch` and storing the value in component state bypasses every
 * intermediate Field/Context layer and just listens to raw store updates.
 */
export const TranslatedField: React.FC<TranslatedFieldProps> = ({
  languages,
  fieldPath,
  multiline,
}) => {
  const form = Form.useFormInstance();
  const fieldContext = useContext(FieldContext as React.Context<any>);
  const prefix: (string | number)[] = Array.isArray(fieldContext?.prefixName)
    ? fieldContext.prefixName
    : [];
  const absolutePath: (string | number)[] = [...prefix, ...fieldPath];

  const [currentLang, setCurrentLang] = useState<string>("");

  // Initialize currentLang to the first available language once they load.
  useEffect(() => {
    if (!currentLang && languages.length > 0) {
      setCurrentLang(languages[0].lang_code);
    }
  }, [languages, currentLang]);

  // Subscribe directly to the form's raw value updates. registerWatch is the
  // low-level hook used by Form.useWatch internally; calling it here keeps the
  // notification path identical regardless of Modal/Form.List nesting.
  const [, bump] = useReducer((x: number) => x + 1, 0);
  const pathKey = absolutePath.join(" ");
  useEffect(() => {
    if (!form) return;
    const internal = (form as any).getInternalHooks?.(HOOK_MARK);
    if (!internal?.registerWatch) return;
    const unsubscribe = internal.registerWatch(() => {
      bump();
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, pathKey]);

  if (languages.length === 0) return null;

  // Read the current value of every language at render time. Subscribed via
  // registerWatch above, so every form mutation triggers a re-render.
  const values: Record<string, string> | undefined = form
    ? (getDeep(form.getFieldsValue(true), absolutePath) as Record<string, string> | undefined)
    : undefined;

  const isFilled = (code: string) => {
    const v = values?.[code];
    return typeof v === "string" && v.trim().length > 0;
  };
  const filledCount = languages.reduce(
    (acc, l) => (isFilled(l.lang_code) ? acc + 1 : acc),
    0
  );

  return (
    <div>
      {/* One Form.Item per language. All stay mounted (the inactive ones are
          hidden via the `hidden` prop) so antd's getFieldsValue() picks them
          all up at submit time. The active item has marginBottom:0 so the
          tag row sits flush against the input. */}
      {languages.map((lang) => {
        const isActive = lang.lang_code === currentLang;
        return (
          <Form.Item
            key={lang.lang_code}
            name={[...fieldPath, lang.lang_code]}
            hidden={!isActive}
            style={isActive ? { marginBottom: 0 } : undefined}
          >
            {multiline ? (
              <Input.TextArea rows={3} placeholder={lang.name} />
            ) : (
              <Input addonBefore={lang.lang_code} placeholder={lang.name} />
            )}
          </Form.Item>
        );
      })}

      <div
        style={{
          marginTop: 4,
          display: "flex",
          alignItems: "center",
          gap: 4,
          flexWrap: "wrap",
        }}
      >
        {languages.map((lang) => {
          const filled = isFilled(lang.lang_code);
          const active = lang.lang_code === currentLang;
          return (
            <Tag
              key={lang.lang_code}
              color={filled ? "success" : "default"}
              onClick={() => setCurrentLang(lang.lang_code)}
              style={{
                cursor: "pointer",
                userSelect: "none",
                fontWeight: active ? 600 : 400,
                border: active ? "1px solid #1890ff" : undefined,
                margin: 0,
              }}
            >
              {lang.lang_code}
            </Tag>
          );
        })}
        {languages.length > 1 && (
          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
            {filledCount}/{languages.length}
          </Text>
        )}
      </div>
    </div>
  );
};
